import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import { EChartsMapSimple } from './EChartsMapSimple';
import { ErrorBoundary } from './ErrorBoundary';
import { ProvinceBlocksContainer } from './ProvinceBlocksContainer';
import { GameControls } from './GameControls';
import { QuizModal } from './QuizModal';
import { ResponsiveDebugger } from './ResponsiveDebugger';
import { ResolutionDisplay } from './ResolutionDisplay';
import { MapSizeMonitor } from './MapSizeMonitor';
import { Leaderboard } from './Leaderboard';
import { useGameStore } from '../store/gameStore';
import { preloadMapData, clearCache } from '../services/mapService';

export function MainApp() {
  const { 
    updateTimer, 
    isGameStarted, 
    mode, 
    difficulty,
    currentQuiz,
    completedProvinces,
    provinces,
    loadMapData,
    getSVGStatus,
    showAbbreviations
  } = useGameStore();
  
  const [hintedProvinceId, setHintedProvinceId] = useState<string | null>(null);
  const [showProvinceNames, setShowProvinceNames] = useState(false);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isRefreshingMap, setIsRefreshingMap] = useState(false);
  
  const refreshMapData = async () => {
    if (isRefreshingMap) return;
    
    try {
      setIsRefreshingMap(true);
      console.log('🔄 开始刷新地图数据...');
      
      clearCache();
      console.log('🗑️ 地图缓存已清除');
      
      await preloadMapData();
      console.log('📥 地图数据重新预加载完成');
      
      await loadMapData();
      console.log('🗺️ 游戏状态地图数据重新加载完成');
      
      toast.success('🎉 地图数据刷新成功！', {
        duration: 2000,
      });
      
      console.log('✅ 地图数据刷新完成');
    } catch (error) {
      console.error('❌ 地图数据刷新失败:', error);
      toast.error('地图数据刷新失败，请稍后重试', {
        duration: 3000,
      });
    } finally {
      setIsRefreshingMap(false);
    }
  };
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ 应用初始化超时，强制完成');
      setIsAppLoading(false);
    }, 10000);

    const initializeApp = async () => {
      try {
        console.log('🚀 开始应用初始化（本地数据优先）...');
        setIsAppLoading(true);
        
        console.log('📥 预加载地图数据...');
        await preloadMapData();
        console.log('✅ 地图数据预加载完成');
        
        console.log('🗺️ 加载游戏状态地图数据...');
        await loadMapData();
        console.log('✅ 游戏状态地图数据加载完成');
        
        console.log('🎉 应用初始化完成');
      } catch (error) {
        console.error('❌ 应用初始化失败:', error);
        toast.error('地图数据加载失败，部分功能可能受限', {
          duration: 5000,
        });
      } finally {
        console.log('🏁 设置应用加载状态为false');
        clearTimeout(timeoutId);
        setIsAppLoading(false);
        console.log('✅ 应用初始化流程完成');
      }
    };

    initializeApp();
  }, [loadMapData]);
  
  useEffect(() => {
    if (!isGameStarted || mode === 'free') return;
    
    const timer = setInterval(() => {
      updateTimer();
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isGameStarted, mode, updateTimer]);
  
  useEffect(() => {
    if (completedProvinces.length === provinces.length && provinces.length > 0) {
      const difficultyNames = {
        easy: '简单',
        medium: '中等', 
        hard: '困难'
      };
      
      toast.success(`🎉 恭喜完成${difficultyNames[difficulty]}难度的所有拼图！`, {
        duration: 4000,
        position: 'top-center',
      });
    }
  }, [completedProvinces.length, provinces.length, difficulty]);
  
  useEffect(() => {
    if (isGameStarted && completedProvinces.length === 0 && provinces.length > 0) {
      const difficultyNames = {
        easy: '简单',
        medium: '中等', 
        hard: '困难'
      };
      
      toast.success(`🚀 开始挑战！当前难度：${difficultyNames[difficulty]}`, {
        duration: 3000,
        position: 'top-center',
        icon: '🎮',
      });
    }
  }, [difficulty, isGameStarted, completedProvinces.length, provinces.length]);
  
  const handleHintUsed = (provinceId: string) => {
    setHintedProvinceId(provinceId);
    
    const province = provinces.find(p => p.id === provinceId);
    if (province) {
      toast(`提示: ${province.name} 的位置已高亮显示`, {
        duration: 3000,
        position: 'top-center',
        icon: '💡',
      });
    }
    
    setTimeout(() => {
      setHintedProvinceId(null);
    }, 3000);
  };

  const handleProvinceClick = (provinceId: string) => {
    const province = provinces.find(p => p.id === provinceId);
    if (province && !completedProvinces.includes(provinceId)) {
      toast(`点击了 ${province.name}`, {
        duration: 1500,
        position: 'bottom-left',
        icon: 'ℹ️',
      });
    }
  };

  if (isAppLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-green-100 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="text-6xl mb-6"
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            🌍
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            正在初始化地图数据...
          </h2>
          <p className="text-gray-600 mb-4">
            优先从本地文件获取真实的中国地图数据
          </p>
          <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-green-100">
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full px-2 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🗺️</span>
              <div>
                <h1 className="text-lg lg:text-xl font-bold text-gray-900">
                  中国地图拼图游戏
                </h1>
                <p className="text-xs text-gray-600 hidden lg:block">
                  基于本地地图数据的交互式地理学习游戏
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <motion.button
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isRefreshingMap 
                    ? 'bg-blue-500 text-white cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={refreshMapData}
                disabled={isRefreshingMap}
                whileHover={!isRefreshingMap ? { scale: 1.05 } : {}}
                whileTap={!isRefreshingMap ? { scale: 0.95 } : {}}
              >
                {isRefreshingMap ? (
                  <>
                    <span className="inline-block animate-spin mr-1">🔄</span>
                    刷新中...
                  </>
                ) : (
                  '🔄 刷新地图'
                )}
              </motion.button>
              
              <ResponsiveDebugger />
              
              {isGameStarted && (
                <motion.button
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    showProvinceNames 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => setShowProvinceNames(!showProvinceNames)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  🏷️ {showProvinceNames ? '隐藏' : '显示'}省名
                </motion.button>
              )}
              
              <div className="text-sm text-gray-500">
                v2.4.0 (本地优先)
              </div>
            </div>
          </div>
        </div>
      </motion.header>
      
      <main className="w-full px-2 py-2">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-2 items-start">
          <motion.div
            className="lg:col-span-1 order-1 lg:order-1"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="sticky top-2 max-h-screen overflow-y-auto space-y-4">
              <GameControls onHintUsed={handleHintUsed} />
              {!isGameStarted && <Leaderboard />}
            </div>
          </motion.div>
          
          <motion.div
            className="lg:col-span-8 order-2 lg:order-2"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <ErrorBoundary fallback={
                <div className="flex items-center justify-center h-[1100px] bg-gray-50">
                  <div className="text-center">
                    <div className="text-4xl mb-4">🗺️</div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">地图组件出错</h3>
                    <p className="text-gray-500 mb-4">正在尝试恢复...</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      刷新页面
                    </button>
                  </div>
                </div>
              }>
                <EChartsMapSimple
                  hintedProvinceId={hintedProvinceId}
                  onProvinceClick={handleProvinceClick}
                />
              </ErrorBoundary>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-1 order-3 lg:order-3"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className="sticky top-2 max-h-screen overflow-y-auto">
              <ProvinceBlocksContainer
                showProvinceNames={showProvinceNames}
                showAbbreviations={showAbbreviations}
                hintedProvinceId={hintedProvinceId}
              />
            </div>
          </motion.div>
        </div>
        
        {!isGameStarted && (
          <motion.div
            className="mt-8 bg-white rounded-xl shadow-lg p-6"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-blue-800 mb-2">🎮 游戏状态</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <div>省份数据: {provinces.length > 0 ? `✅ 已加载 ${provinces.length} 个省份` : '❌ 未加载'}</div>
                <div>游戏状态: {isGameStarted ? '✅ 已开始' : '❌ 未开始'}</div>
                <div>完成进度: {completedProvinces.length}/{provinces.length} ({provinces.length > 0 ? Math.round((completedProvinces.length / provinces.length) * 100) : 0}%)</div>
                <div>提示状态: {hintedProvinceId ? `💡 ${provinces.find(p => p.id === hintedProvinceId)?.name || '未知'}` : '❌ 无'}</div>
                <div>地图刷新: {isRefreshingMap ? '🔄 刷新中...' : '✅ 正常'}</div>
                
                {(() => {
                  const svgStatus = getSVGStatus();
                  return (
                    <div>SVG文件: {svgStatus.total > 0 ? `🎨 已缓存 ${svgStatus.loaded}/${svgStatus.total} (失败: ${svgStatus.failed})` : '❌ 未加载'}</div>
                  );
                })()}
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-gray-800 mb-4">游戏特色 v2.2 (简洁版)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-3">🎨</div>
                <h3 className="font-semibold text-gray-700 mb-2">颜色状态</h3>
                <p className="text-sm text-gray-600">
                  地图省份根据状态自动显示颜色：红色待拼图，绿色已完成，蓝色非游戏区域。
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="font-semibold text-gray-700 mb-2">简洁操作</h3>
                <p className="text-sm text-gray-600">
                  专注核心功能，拖拽省份到地图对应位置，实时颜色反馈。
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🚀</div>
                <h3 className="font-semibold text-gray-700 mb-2">流畅体验</h3>
                <p className="text-sm text-gray-600">
                  优化性能，快速响应，专注于地图省份状态的直观显示。
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">v2.2 简洁版特点</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• 🎨 核心功能：地图省份颜色状态自动更新</li>
                <li>• 🔴 待拼图省份：红色显示，清晰标识需要完成的区域</li>
                <li>• 🟢 已完成省份：绿色显示，并显示省份名称</li>
                <li>• 🔵 非游戏省份：浅蓝色显示，区分当前难度范围</li>
                <li>• ⚡ 性能优化：简化逻辑，专注核心体验</li>
                <li>• 📱 响应式设计：适配各种设备尺寸</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">💡</span>
                <div className="text-sm text-green-800">
                  <strong>省份颜色说明：</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• 🔴 红色区域：需要拼图的省份</li>
                    <li>• 🟢 绿色区域：已完成的省份（显示省名）</li>
                    <li>• 🔵 蓝色区域：当前难度不需要拼图的省份</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
      
      {isGameStarted && <ResolutionDisplay position="bottom-right" />}
      {isGameStarted && <MapSizeMonitor position="bottom-left" />}
      {currentQuiz && <QuizModal />}
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#333',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            borderRadius: '12px',
            padding: '12px 16px',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <footer className="mt-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-600">
            <p>© 2024 中国地图拼图游戏 v2.4.0 - 本地优先数据加载</p>
            <p className="mt-1">
              Made with ❤️ using React + TypeScript + ECharts + Tailwind CSS
            </p>
            <p className="mt-1 text-xs text-gray-500">
              地图数据来源: 本地文件 (备用: <a href="https://datav.aliyun.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">阿里云DataV</a>)
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
